import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const HTMLSyntax = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Foundation Mastery</div>
                <h1>HTML Syntax & Document Structure</h1>
                <p className="hero-lead">
                    Master the fundamental rules and structure of HTML. Understanding syntax is crucial for writing
                    valid, maintainable code that works across all browsers.
                </p>
            </div>

            <div className="content-section">
                <h2>1. The Philosophical Foundation: HTML5 vs. Predecessors</h2>
                <p>
                    To truly master HTML syntax, one must understand the philosophical shift that occurred with
                    the transition to HTML5. In the early 2000s, the web was moving toward <strong>XHTML</strong>
                    (Extensible HyperText Markup Language), which brought the strict, unforgiving rules of XML
                    to the browser. If a developer forgot a single closing tag, the browser would refuse to
                    render the page, showing a "Yellow Screen of Death."
                </p>
                <ProTip><p><strong>Senior Insight:</strong> HTML5 chose a "Postel's Law" approach: <em>"Be conservative
                        in what you do, be liberal in what you accept from others."</em> Modern HTML5 is designed to
                        be extremely fault-tolerant, but as professional engineers, we aim for "Well-Formed" markup
                        to ensure predictability and speed.</p></ProTip>
                <p>
                    Today, we follow the <strong>Living Standard</strong>. This means HTML is no longer a versioned
                    language (like HTML 4.01), but a constantly evolving specification that prioritizes
                    interoperability over rigid academic purity.
                </p>
            </div>

            <div className="content-section">
                <h2>2. HTML Syntax Fundamentals: Tags, Elements, and DOM Nodes</h2>
                <p>
                    In casual conversation, developers often use "tag" and "element" interchangeably. However,
                    understanding the technical difference is vital for advanced debugging and JavaScript
                    manipulation.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <div className="card-icon"></div>
                        <h3>The Tag</h3>
                        <p>
                            Think of the <strong>Tag</strong> as the "instruction." It exists in your source code.
                            <code>&lt;p&gt;</code> is an instruction to the browser that "a paragraph starts here."
                        </p>
                    </div>
                    <div className="concept-card">
                        <div className="card-icon"></div>
                        <h3>The Element</h3>
                        <p>
                            The <strong>Element</strong> is the resulting object. It is the combination of the
                            Opening Tag, the Content, and the Closing Tag.
                        </p>
                    </div>
                    <div className="concept-card">
                        <div className="card-icon"></div>
                        <h3>The DOM Node</h3>
                        <p>
                            Once the browser parses an element, it becomes a <strong>DOM Node</strong> in memory.
                            This is the live object you interact with via JavaScript <code>document.querySelector</code>.
                        </p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Anatomy of an HTML Element</h2>
                <div className="diagram-box">
                    <CodeBlock language="html" code={`<tagname attribute="value">Content goes here</tagname>
                                               
Opening  Attribute  Value      Content        Closing
  Tag      Name                                  Tag`} />
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Complete Element Example</span>
                    </div>
                    <CodeBlock language="html" code={`<a href="https://example.com" target="_blank" title="Visit Example">
    Click here
</a>`} />
                </div>

                <div className="explanation-list">
                    <h3>Breaking it down:</h3>
                    <ul>
                        <li><code>&lt;a&gt;</code> - Opening tag (anchor element)</li>
                        <li><code>href="..."</code> - Attribute (destination URL)</li>
                        <li><code>target="_blank"</code> - Another attribute (open in new tab)</li>
                        <li><code>title="..."</code> - Tooltip text</li>
                        <li><code>Click here</code> - Element content</li>
                        <li><code>&lt;/a&gt;</code> - Closing tag</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Opening and Closing Tags</h2>
                <p>
                    Most HTML elements have both opening and closing tags. The closing tag includes a forward slash (<code>/</code>).
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Paired Tags</span>
                    </div>
                    <CodeBlock language="html" code={`<h1>This is a heading</h1>
<p>This is a paragraph</p>
<div>This is a container</div>
<span>This is inline text</span>`} />
                </div>

                <Warning><strong> Common Mistake:</strong> Forgetting the closing tag leads to unexpected layout issues.
                    Modern browsers try to fix it, but don't rely on their error correction!</Warning>
            </div>

            <div className="content-section">
                <h2>Self-Closing (Void) Elements</h2>
                <p>
                    Some elements don't have content and don't need closing tags. These are called <strong>void elements</strong> or <strong>empty elements</strong>.
                </p>

                <div className="comparison-table">
                    <h3>Common Void Elements:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Element</th>
                                <th>HTML5 Syntax</th>
                                <th>XHTML Syntax</th>
                                <th>Purpose</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>&lt;br&gt;</code></td>
                                <td><code>&lt;br&gt;</code></td>
                                <td><code>&lt;br /&gt;</code></td>
                                <td>Line break</td>
                            </tr>
                            <tr>
                                <td><code>&lt;img&gt;</code></td>
                                <td><code>&lt;img src="..." alt="..."&gt;</code></td>
                                <td><code>&lt;img ... /&gt;</code></td>
                                <td>Image</td>
                            </tr>
                            <tr>
                                <td><code>&lt;hr&gt;</code></td>
                                <td><code>&lt;hr&gt;</code></td>
                                <td><code>&lt;hr /&gt;</code></td>
                                <td>Horizontal rule</td>
                            </tr>
                            <tr>
                                <td><code>&lt;input&gt;</code></td>
                                <td><code>&lt;input type="text"&gt;</code></td>
                                <td><code>&lt;input ... /&gt;</code></td>
                                <td>Form input</td>
                            </tr>
                            <tr>
                                <td><code>&lt;meta&gt;</code></td>
                                <td><code>&lt;meta charset="UTF-8"&gt;</code></td>
                                <td><code>&lt;meta ... /&gt;</code></td>
                                <td>Metadata</td>
                            </tr>
                            <tr>
                                <td><code>&lt;link&gt;</code></td>
                                <td><code>&lt;link rel="stylesheet"&gt;</code></td>
                                <td><code>&lt;link ... /&gt;</code></td>
                                <td>External resource</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>HTML5 Note:</strong> In HTML5, the trailing slash (<code>/&gt;</code>) is optional.
                        Both <code>&lt;br&gt;</code> and <code>&lt;br /&gt;</code> are valid. Many developers use the slash
                        for clarity and XHTML compatibility.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>4. The Art of Nesting: Content Models and Hierarchy</h2>
                <p>
                    Elements can be placed inside other elements, creating a tree-like structure known as the
                    <strong>DOM Tree</strong>. However, nesting is not a "free-for-all." HTML5 defines
                    <strong>Content Models</strong> that dictate which elements can legally live inside others.
                </p>

                <ProTip><p><strong>The Golden Rule of HTML Nesting:</strong> "Block-level" elements (like
                        <code>&lt;div&gt;</code>) should not typically be nested inside "Inline" elements
                        (like <code>&lt;span&gt;</code>). While browsers might render it, it violates the
                        spec and can break your layout in subtle ways.</p></ProTip>

                <div className="code-example">
                    <div className="code-header">
                        <span className="correct">Semantic Nesting</span>
                    </div>
                    <CodeBlock language="html" code={`<div>
    <h2>Article Title</h3>
    <p>This is a paragraph with <strong>bold text</strong>.</p>
</div>`} />
                </div>

                <Warning><p><strong>Nesting Errors & Browser "Fixes":</strong> If you overlap tags (e.g.,
                        <code>&lt;b&gt;&lt;i&gt;text&lt;/b&gt;&lt;/i&gt;</code>), the browser's pre-parser
                        will actually reconstruct the DOM to "fix" your mistake by splitting the nodes. This
                        is expensive and can lead to bugs in your JavaScript selectors!</p></Warning>

                <div className="visualization">
                    <h3>The DOM Hierarchy Visualization:</h3>
                    <CodeBlock language="html" code={`<body> (Parent)
 <header> (Child of Body)
    <h1> (Grandchild of Body)
    <nav> (Sibling of H1)
 <main> (Sibling of Header)
     <p> (Child of Main)`} />
                </div>
            </div>

            <div className="content-section">
                <h2>5. Internal Mechanics: How Browsers Parse Attributes</h2>
                <p>
                    Attributes are the metadata of an element. While they look like simple key-value pairs,
                    the browser's parsing engine (like Chromium's Blink) follows specific rules for
                    <strong>canonicalization</strong>.
                </p>

                <ProTip><p><strong>The "Presence" Rule for Boolean Attributes:</strong> In HTML5, for boolean
                        attributes like <code>disabled</code>, <code>checked</code>, or <code>readonly</code>,
                        the <em>value</em> does not matter. If the attribute name is present, the property is
                        set to <strong>true</strong>.</p></ProTip>

                <div className="code-example">
                    <div className="code-header">
                        <span>Boolean Attribute Confusion</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- All of these result in a DISABLED input --&gt;
<input type="text" disabled>
<input type="text" disabled="disabled">
<input type="text" disabled="true">
<input type="text" disabled="false"> <!-- STILL DISABLED! --&gt;

<!-- Only removing the attribute makes it enabled --&gt;
<input type="text">`} />
                </div>

                <p className="mt-4">
                    <strong>Attribute Ordering:</strong> The order of attributes within a tag does not matter
                    for the final render, but the <strong>first occurrence wins</strong> if an attribute is
                    duplicated. For example, in <code>&lt;div id="first" id="second"&gt;</code>, the ID
                    will be "first" in the DOM.
                </p>
            </div>

            <div className="content-section">
                <h2>6. Syntax Rules & Edge Cases</h2>

                <div className="syntax-rules">
                    <h3>Attribute Syntax Rules:</h3>
                    <ul>
                        <li><strong>Space before attribute:</strong> <code>&lt;a href="..."&gt;</code> not <code>&lt;ahref="..."&gt;</code></li>
                        <li><strong>Equals sign:</strong> <code>href="value"</code> not <code>href = "value"</code> (no spaces around =)</li>
                        <li><strong>Quotes:</strong> Use double quotes <code>"value"</code> or single quotes <code>'value'</code></li>
                        <li><strong>Multiple attributes:</strong> Separate with spaces: <code>&lt;img src="..." alt="..."&gt;</code></li>
                    </ul>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Attribute Examples</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Multiple attributes --&gt;
<img src="logo.png" alt="Company Logo" width="200" height="100">

<!-- Single vs Double Quotes (both valid) --&gt;
<a href="page.html" title="Link title">Link</a>
<a href='page.html' title='Link title'>Link</a>

<!-- Attributes without quotes (HTML5 allows in simple cases) --&gt;
<input type=text name=username>
<!-- But quotes are safer and recommended! --&gt;

<!-- Boolean attributes (presence = true) --&gt;
<input type="checkbox" checked>
<input type="text" disabled>
<script src="app.js" defer></script>`} />
                </div>

                <div className="common-attributes">
                    <h3>Universal Attributes (work on any element):</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Attribute</th>
                                <th>Purpose</th>
                                <th>Example</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>id</code></td>
                                <td>Unique identifier</td>
                                <td><code>&lt;div id="header"&gt;</code></td>
                            </tr>
                            <tr>
                                <td><code>class</code></td>
                                <td>CSS classes (can repeat)</td>
                                <td><code>&lt;p class="intro highlight"&gt;</code></td>
                            </tr>
                            <tr>
                                <td><code>style</code></td>
                                <td>Inline CSS (avoid!)</td>
                                <td><code>&lt;p style="color: red;"&gt;</code></td>
                            </tr>
                            <tr>
                                <td><code>title</code></td>
                                <td>Tooltip text</td>
                                <td><code>&lt;abbr title="HyperText Markup Language"&gt;HTML&lt;/abbr&gt;</code></td>
                            </tr>
                            <tr>
                                <td><code>lang</code></td>
                                <td>Content language</td>
                                <td><code>&lt;html lang="en"&gt;</code></td>
                            </tr>
                            <tr>
                                <td><code>data-*</code></td>
                                <td>Custom data attributes</td>
                                <td><code>&lt;div data-user-id="123"&gt;</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>HTML Comments</h2>
                <p>
                    Comments are not displayed in the browser but help document your code. Use them to explain complex sections
                    or temporarily disable code.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Comment Syntax</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- This is a comment --&gt;

<!-- 
    Multi-line comment
    Can span multiple lines
--&gt;

<p>This text is visible</p>
<!-- <p>This text is hidden</p> --&gt;`} />
                </div>

                <div className="tip-box">
                    <strong> Best Practices:</strong>
                    <ul>
                        <li>Use comments sparinglygood code is self-documenting</li>
                        <li>Explain "why", not "what" (code shows what it does)</li>
                        <li>Remove commented-out code before deploying to production</li>
                        <li>Comments are visible in page sourcedon't include sensitive information!</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Case Sensitivity</h2>
                <p>
                    HTML is <strong>case-insensitive</strong> for tag names and attributes, but lowercase is conventional and required for XHTML compatibility.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>All Valid (but use lowercase!)</span>
                    </div>
                    <CodeBlock language="html" code={`<P>Paragraph</P>
<p>Paragraph</p>
<P>Paragraph</p>

<IMG SRC="image.jpg" ALT="Image">
<img src="image.jpg" alt="Image">  <!-- Preferred --&gt;`} />
                </div>

                <Warning><strong> Exception:</strong> Attribute <em>values</em> ARE case-sensitive in some contexts.
                    For example, <code>id="MyDiv"</code> and <code>id="mydiv"</code> are different.</Warning>
            </div>

            <div className="content-section">
                <h2>12. Whitespace Handling: The Rendering Engine Perspective</h2>
                <p>
                    One of the most confusing aspects for new developers is how HTML handles tabs, spaces, and
                    newlines. In the world of the <strong>Critical Rendering Path</strong>, the browser's
                    HTML parser ignores "unnecessary" whitespace between elements to save memory and processing
                    cycles.
                </p>

                <ProTip><p><strong>The "One Space" Rule:</strong> No matter how many times you press the spacebar
                        or the enter key between two words inside a tag, the browser will collapse them into a
                        single space character during the creation of the Render Tree.</p></ProTip>

                <div className="code-example">
                    <div className="code-header">
                        <span>Formatting vs. Rendering</span>
                    </div>
                    <CodeBlock language="html" code={`<p>This         is
spaced      out
    in code.</p>`} />
                    <div className="code-output">
                        <p>Renders as: <strong>This is spaced out in code.</strong></p>
                    </div>
                </div>

                <p className="mt-4">
                    <strong>When to avoid collapsing:</strong> If you are displaying code snippets, poetry, or
                    ASCII art, you must use the <code>&lt;pre&gt;</code> element. This tells the browser:
                    "Do not touch the whitespace; the formatting here is data."
                </p>
            </div>

            <div className="content-section">
                <h2>13. The DOCTYPE: Why It Is Technically Not a Tag</h2>
                <p>
                    The <code>&lt;!DOCTYPE html&gt;</code> is often mistaken for an HTML tag, but it is technically
                    a <strong>Document Type Declaration (DTD)</strong>. It is a set of instructions to the
                    browser's rendering engine that existed before HTML was a standalone standard.
                </p>
                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Mode</th>
                                <th>Trigger</th>
                                <th>Behavior</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Standards Mode</strong></td>
                                <td>Modern <code>&lt;!DOCTYPE html&gt;</code></td>
                                <td>Browser follows modern CSS/HTML specifications perfectly.</td>
                            </tr>
                            <tr>
                                <td><strong>Quirks Mode</strong></td>
                                <td>Missing DOCTYPE</td>
                                <td>Browser mimics bugs in Internet Explorer 5 to avoid breaking 90s websites.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <ProTip><p><strong>Warning:</strong> Forgetting the DOCTYPE can trigger "Quirks Mode," which
                        significantly alters how CSS layout (like margins and padding) is calculated. Always
                        ensure it is the very first line of your document.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>10. Special Characters & Entities: The Security Perspective</h2>
                <p>
                    HTML entities like <code>&amp;lt;</code> and <code>&amp;gt;</code> are more than just
                    conveniences for displaying symbols; they are the front line of <strong>XSS Prevention</strong>.
                    When a browser sees <code>&lt;script&gt;</code>, it executes it. When it sees
                    <code>&amp;lt;script&amp;gt;</code>, it renders it as harmless text.
                </p>

                <ProTip><p><strong>Security Tip:</strong> Always "escape" or "encode" data that comes from users
                        before displaying it back on a page. If you forget to encode the <code>&lt;</code> character,
                        an attacker can inject malicious JavaScript into your site.</p></ProTip>

                <div className="entity-table">
                    <h3>Critical Entities for Security:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Character</th>
                                <th>Entity</th>
                                <th>Context</th>
                                <th>Reason for Encoding</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>&lt;</code></td>
                                <td><code>&amp;lt;</code></td>
                                <td>Inside Tag Content</td>
                                <td>Prevents the start of a new tag.</td>
                            </tr>
                            <tr>
                                <td><code>&gt;</code></td>
                                <td><code>&amp;gt;</code></td>
                                <td>Inside Tag Content</td>
                                <td>Prevents the closing of a tag prematurely.</td>
                            </tr>
                            <tr>
                                <td><code>&amp;</code></td>
                                <td><code>&amp;amp;</code></td>
                                <td>Anywhere</td>
                                <td>Prevents the start of an entity or character reference.</td>
                            </tr>
                            <tr>
                                <td><code>"</code></td>
                                <td><code>&amp;quot;</code></td>
                                <td>Inside Attributes</td>
                                <td>Prevents escaping an attribute value: <code>value="&amp;quot; onclick=..."</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>11. Case Sensitivity: The "Mixed Mode" Reality</h2>
                <p>
                    A common piece of advice is: "HTML is case-insensitive." While technically true for
                    <strong>Tag Names</strong> and <strong>Standard Attributes</strong>, this is a
                    dangerous oversimplification for a modern developer.
                </p>
                <div className="comparison-grid">
                    <div className="comp-item">
                        <h4>Case-Insensitive (Safe)</h4>
                        <ul>
                            <li><code>&lt;DIV&gt;</code> vs <code>&lt;div&gt;</code></li>
                            <li><code>HREF</code> vs <code>href</code></li>
                            <li><code>METHOD="POST"</code> vs <code>method="post"</code></li>
                        </ul>
                    </div>
                    <div className="comp-item warning">
                        <h4>Case-Sensitive (Dangerous)</h4>
                        <ul>
                            <li><strong>ID values:</strong> <code>#MyElement</code> is not the same as <code>#myelement</code> in CSS/JS.</li>
                            <li><strong>Class names:</strong> <code>.active</code> is distinct from <code>.Active</code>.</li>
                            <li><strong>Entity Names:</strong> <code>&amp;copy;</code> works, but <code>&amp;COPY;</code> might not.</li>
                            <li><strong>Data Attributes:</strong> <code>data-UserID</code> becomes <code>dataset.userid</code> in JS (canonicalized to lowercase).</li>
                        </ul>
                    </div>
                </div>
                <ProTip><p><strong>Professional Standard:</strong> Always use <strong>all-lowercase</strong> for
                        tags and attributes. This ensures compatibility with XHTML, consistency in your CSS
                        selectors, and keeps your codebase clean for automated tooling.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>Valid HTML Document Structure</h2>
                <p>
                    Every HTML5 document should follow this basic structure:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Complete HTML5 Template</span>
                    </div>
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="description" content="Page description for SEO">
    <title>Page Title</title>
    <link rel="stylesheet" href="styles.css">
</head>
<body>
    <!-- Page content goes here --&gt;
    <h1>Main Heading</h1>
    <p>Paragraph text.</p>
    
    <script src="script.js"></script>
</body>
</html>`} />
                </div>

                <div className="explanation-list">
                    <h3>Required Elements:</h3>
                    <ol>
                        <li><code>&lt;!DOCTYPE html&gt;</code> - Declares HTML5 (must be first line)</li>
                        <li><code>&lt;html&gt;</code> - Root element, wraps everything</li>
                        <li><code>&lt;head&gt;</code> - Metadata (not visible on page)</li>
                        <li><code>&lt;title&gt;</code> - Page title (browser tab)</li>
                        <li><code>&lt;body&gt;</code> - Visible page content</li>
                    </ol>
                </div>
            </div>

            <div className="content-section">
                <h2>HTML Validation</h2>
                <p>
                    Valid HTML follows all syntax rules. Use validators to check for errors:
                </p>

                <div className="tool-list">
                    <ul>
                        <li><strong>W3C Markup Validation Service:</strong> <a href="https://validator.w3.org/" target="_blank" rel="noopener noreferrer">validator.w3.org</a></li>
                        <li><strong>Browser DevTools:</strong> Console shows HTML errors</li>
                        <li><strong>IDE Extensions:</strong> VSCode, WebStorm have built-in validators</li>
                    </ul>
                </div>

                <Warning><strong> Why Validation Matters:</strong>
                    <ul>
                        <li>Ensures cross-browser compatibility</li>
                        <li>Improves accessibility</li>
                        <li>Better SEO rankings</li>
                        <li>Easier maintenance and debugging</li>
                    </ul></Warning>
            </div>

            <div className="content-section">
                <h2>Best Practices Summary</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use lowercase for tags and attributes</li>
                            <li>Always close tags (except void elements)</li>
                            <li>Quote attribute values</li>
                            <li>Nest elements properly</li>
                            <li>Use semantic elements</li>
                            <li>Validate your HTML</li>
                            <li>Indent for readability</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Mix uppercase and lowercase randomly</li>
                            <li>Overlap tags incorrectly</li>
                            <li>Omit quotes around complex attributes</li>
                            <li>Use inline styles excessively</li>
                            <li>Forget alt text on images</li>
                            <li>Use deprecated tags (<code>&lt;font&gt;</code>, <code>&lt;center&gt;</code>)</li>
                            <li>Include sensitive info in comments</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>14. Thinking in Syntax: A Professional Summary</h2>
                <p>
                    HTML syntax is the bedrock of the entire web ecosystem. While the language is designed to
                    "not break" when you make a mistake, professional engineering requires a higher standard.
                    Writing valid, well-commented, and properly nested code ensures that:
                </p>
                <div className="concept-grid">
                    <div className="concept-card">
                        <h4>Future Compatibility</h4>
                        <p>Code that is valid today is much more likely to render correctly in browsers
                            released ten years from now.</p>
                    </div>
                    <div className="concept-card">
                        <h4>Search Engine Precision</h4>
                        <p>Crawlers like Googlebot can more accurately index your content when it follows
                            a clear, error-free hierarchy.</p>
                    </div>
                </div>
                <p className="mt-4">
                    As you move forward, keep the <strong>Living Standard</strong> in mind. Your role is not
                    just to make things appearance correctly on your screen, but to create a semantic
                    document that any machine or assistive device can interpret without ambiguity.
                </p>
            </div>

        </div>
    );
};

export default HTMLSyntax;



